import { AlertTriangle, Info, CheckCircle, XCircle, Clock } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '../ui/alert';
import { Button } from '../ui/button';

interface AlertCardProps {
  id: string;
  type: 'error' | 'warning' | 'info' | 'success';
  title: string;
  message: string;
  timestamp?: string;
  actionLabel?: string;
  onAction?: () => void;
  onDismiss?: () => void;
  showDismiss?: boolean;
  className?: string;
}

export function AlertCard({
  id,
  type,
  title,
  message,
  timestamp,
  actionLabel,
  onAction,
  onDismiss,
  showDismiss = true,
  className = ''
}: AlertCardProps) {
  const getAlertConfig = (type: string) => {
    switch (type) {
      case 'error':
        return {
          variant: 'destructive' as const,
          icon: XCircle,
          borderClass: 'border-l-4 border-l-destructive'
        };
      case 'warning':
        return {
          variant: 'default' as const,
          icon: AlertTriangle,
          borderClass: 'border-l-4 border-l-[#F07D13] bg-[#F07D13]/5'
        };
      case 'info':
        return {
          variant: 'default' as const,
          icon: Info,
          borderClass: 'border-l-4 border-l-[#14CAF4] bg-[#14CAF4]/5'
        };
      case 'success':
        return {
          variant: 'default' as const,
          icon: CheckCircle,
          borderClass: 'border-l-4 border-l-green-500 bg-green-50'
        };
      default:
        return {
          variant: 'default' as const,
          icon: Info,
          borderClass: 'border-l-4'
        };
    }
  };

  const config = getAlertConfig(type);
  const Icon = config.icon;

  return (
    <Alert variant={config.variant} className={`${config.borderClass} ${className}`}>
      <Icon className="h-4 w-4" />
      <div className="flex-1">
        <AlertTitle>{title}</AlertTitle>
        <AlertDescription>
          {message}
          {timestamp && (
            <div className="flex items-center gap-1 text-muted-foreground mt-2">
              <Clock className="h-3 w-3" />
              {timestamp}
            </div>
          )}
        </AlertDescription>
        {(actionLabel || showDismiss) && (
          <div className="flex items-center gap-2 mt-3">
            {actionLabel && onAction && (
              <Button variant="outline" size="sm" onClick={onAction}>
                {actionLabel}
              </Button>
            )}
            {showDismiss && onDismiss && (
              <Button variant="ghost" size="sm" onClick={onDismiss}>
                Dismiss
              </Button>
            )}
          </div>
        )}
      </div>
    </Alert>
  );
}
